import { useCallback, useState } from 'react'
import {
  Pressable,
  ScrollView,
  StyleSheet,
  Text,
  TouchableOpacity,
  View
} from 'react-native'
import { Ionicons } from '@expo/vector-icons'

// styles
import { colorPalette, globalStyles } from '../../../static/styles/global'

// components
import ModalPopup from '../modal/ModalPopup'
import MyScrollView from '../navigator/MyScrollView'

interface optionsProps {
  title?: string
  options: string[]
  optionLabels?: string[]
  selectedOption: string
  onSelect: any
  status?: any
  textColor?: string
  isDisabled?: boolean
}

const Select = ({
  title,
  options,
  optionLabels,
  selectedOption,
  onSelect,
  status,
  textColor,
  isDisabled = false
}: optionsProps): JSX.Element => {
  const [visible, setVisible] = useState(false)
  const [selectedOptionValue, setSelectedOptionValue] = useState(selectedOption)

  const handleSelectOption = (option: string): void => {
    setVisible(!visible)
    setSelectedOptionValue(option)
    onSelect(option)
  }

  const selectedOptionIndex = options.findIndex(
    (option) => option === selectedOptionValue
  )

  let optionTextColor: { color: string }

  switch (textColor) {
    case 'primary':
      optionTextColor = !isDisabled
        ? globalStyles.primaryTextColor
        : globalStyles.darkTextColor
      break
    case 'secondary':
      optionTextColor = !isDisabled
        ? globalStyles.secondaryTextColor
        : globalStyles.darkTextColor
      break
    case 'tertiary':
      optionTextColor = !isDisabled
        ? globalStyles.tertiaryTextColor
        : globalStyles.darkTextColor
      break
    default:
      optionTextColor = globalStyles.darkTextColor
      break
  }

  return (
    <Pressable
      style={styles.dropdown}
      onPress={() => setVisible(!visible)}
      disabled={isDisabled}
    >
      <Text
        style={[
          globalStyles.input,
          globalStyles.inputLight,
          globalStyles.inputSelect,
          isDisabled && globalStyles.inputReadOnly,
          optionTextColor,
          status?.touched && status?.error && globalStyles.inputError
        ]}
      >
        {selectedOptionValue}
      </Text>
      <View style={styles.dropdownIcon}>
        <Ionicons
          name="chevron-down"
          size={23}
          color={colorPalette.secondary}
        />
      </View>
      <ModalPopup visible={visible} screen={'dropdown'}>
        <View style={styles.modalHeader}>
          <Text
            style={[
              globalStyles.text,
              globalStyles.blackTextColor,
              globalStyles.fs18,
              globalStyles.fwSemiBold
            ]}
          >
            {title}
          </Text>
          <TouchableOpacity onPress={() => setVisible(!visible)}>
            <Ionicons name="close-sharp" size={24} color={colorPalette.black} />
          </TouchableOpacity>
        </View>
        <MyScrollView style={[styles.modalBody]}>
          {options.length > 0 &&
            options.map((option, index) => (
              <TouchableOpacity
                onPress={() => handleSelectOption(option)}
                style={[
                  styles.option,
                  options.length === index + 1 && styles.lastOption
                ]}
                key={option}
              >
                {selectedOptionIndex === index && (
                  <View
                    style={[
                      globalStyles.relativePosition,
                      globalStyles.ln3,
                      globalStyles.mr1
                    ]}
                  >
                    <Ionicons
                      name="checkmark-sharp"
                      size={20}
                      color={colorPalette.secondary}
                    />
                  </View>
                )}
                <Text
                  style={[
                    globalStyles.text,
                    globalStyles.blackTextColor,
                    selectedOptionIndex === index && globalStyles.fwBold
                  ]}
                >
                  {option}
                </Text>
              </TouchableOpacity>
            ))}
        </MyScrollView>
      </ModalPopup>
    </Pressable>
  )
}

const styles = StyleSheet.create({
  dropdown: {
    position: 'relative'
  },
  dropdownIcon: {
    position: 'absolute',
    right: 6,
    top: 12
  },
  option: {
    paddingVertical: 12,
    paddingHorizontal: 16,
    borderBottomWidth: 1,
    borderBottomColor: colorPalette.light,
    flexDirection: 'row',
    justifyContent: 'flex-start',
    alignItems: 'center'
  },
  lastOption: {
    borderBottomWidth: 0
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    width: '100%',
    paddingHorizontal: 16,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderColor: colorPalette.darkLight
  },
  modalBody: {
    paddingVertical: 0,
    paddingHorizontal: 0
  }
})

export default Select
